import { useEffect, useState } from 'react';

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  delay: number;
  duration: number;
  size: number;
  drift: number;
  wobble: number;
  rotation: number;
  shape: 'circle' | 'square' | 'rectangle' | 'star';
}

interface ConfettiProps {
  colors: string[];
  active: boolean;
  onComplete?: () => void;
}

const shapes = ['circle', 'square', 'rectangle', 'star'] as const;

export function Confetti({ colors, active, onComplete }: ConfettiProps) {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);

  useEffect(() => {
    if (active) {
      // Generate more confetti pieces with varied properties
      const newPieces: ConfettiPiece[] = [];
      for (let i = 0; i < 80; i++) {
        newPieces.push({
          id: i,
          x: Math.random() * 100,
          color: colors[Math.floor(Math.random() * colors.length)],
          delay: Math.random() * 0.5,
          duration: 1.5 + Math.random() * 1.5,
          size: 6 + Math.random() * 12,
          drift: (Math.random() - 0.5) * 200, // Horizontal drift in pixels
          wobble: Math.random() * 10, // Wobble amount
          rotation: Math.random() * 360,
          shape: shapes[Math.floor(Math.random() * shapes.length)],
        });
      }
      setPieces(newPieces);

      // Clear after animation
      const timeout = setTimeout(() => {
        setPieces([]);
        onComplete?.();
      }, 3500);

      return () => clearTimeout(timeout);
    }
  }, [active, colors, onComplete]);

  if (!active || pieces.length === 0) return null;

  const getShapeStyles = (piece: ConfettiPiece): React.CSSProperties => {
    const base: React.CSSProperties = {
      backgroundColor: piece.color,
    };
    
    switch (piece.shape) {
      case 'circle':
        return { ...base, borderRadius: '50%' };
      case 'square':
        return { ...base, borderRadius: '2px' };
      case 'rectangle':
        return { 
          ...base, 
          borderRadius: '1px',
          width: piece.size * 0.4,
          height: piece.size,
        };
      case 'star':
        return {
          ...base,
          clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        };
      default:
        return base;
    }
  };

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className="absolute animate-confetti-fall"
          style={{
            left: `${piece.x}%`,
            top: '-20px',
            width: piece.shape === 'rectangle' ? piece.size * 0.4 : piece.size,
            height: piece.size,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
            '--drift': `${piece.drift}px`,
            '--wobble': piece.wobble,
            '--rotation': `${piece.rotation}deg`,
            ...getShapeStyles(piece),
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}
